import { PLAQUE, LABEL } from './hud-theme';
import { t } from '../i18n/i18n';

/**
 * Notifications in the Sconce (2026-09-01).
 *
 * User: "if I complete a quest in the HUB, there should be a push notification
 * and a reward for it."
 *
 * The hub had no notification channel at all — a quest that finished on your
 * last extraction simply appeared as a Claim button inside a panel you had to
 * think to open. Two things were missing: being told, and being able to take
 * the reward without hunting for it.
 *
 * ## Why the reward is a button and not automatic
 *
 * `design/gdd/quests.md` is deliberate that completion and reward are separate
 * — "claiming happens later via REST, entirely decoupled from the Colyseus
 * room", and the whole `COMPLETED → CLAIMED` transition in core exists to make
 * the grant idempotent under concurrent requests. Auto-granting on sight would
 * quietly move that decision into a page load, which is a worse place for it:
 * a failed request would be invisible, and the same completion seen in two
 * tabs would race. So the toast brings the reward to the player and they take
 * it in one tap, which is the "and a reward for it" half without rewriting the
 * economy contract.
 *
 * Unlike the in-run banners this one is interactive, so it is
 * `pointer-events-auto` — but only the card is, never the full-screen layer,
 * which would blanket the hub in an invisible click-eater.
 */

/** Toasts here are read, not glanced at mid-fight, and one carries a button to press. */
const HOLD_MS = 9_000;
const FADE_MS = 300;
/** Returning after a long break can surface several at once; more than this is a wall. */
const MAX_VISIBLE = 3;

export interface HubToastAction {
  label: string;
  /** Resolves when the action succeeded; the card dismisses itself. Rejecting leaves the card up with its message. */
  run: () => Promise<void>;
}

export interface HubToastOptions {
  heading: string;
  title: string;
  detail?: string;
  /** Markup alternative to `detail`, for a reward line carrying currency glyphs. Wins when both are given. */
  detailHtml?: string;
  action?: HubToastAction;
}

export class HubToast {
  private readonly root: HTMLDivElement;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // Bottom-centre on a phone, top-centre on anything wider. The hub's top
    // strip is the logo and the currency pills, which stack into the full
    // width on mobile; its bottom strip is the reverse.
    this.root.className = [
      'fixed', 'z-[2000]', 'pointer-events-none', 'flex', 'flex-col', 'gap-2', 'items-center',
      'left-1/2', '-translate-x-1/2',
      'bottom-[max(0.75rem,env(safe-area-inset-bottom))]',
      // Below the header row, not level with it: at 1280 a top-aligned toast
      // clipped the Wax balance pill. The header is the one strip on this
      // screen with controls in it, so it is the one a toast must not cover.
      'sm:bottom-auto', 'sm:top-[5.25rem]',
      'w-[22rem]', 'max-w-[92vw]',
    ].join(' ');
    container.appendChild(this.root);
  }

  show(options: HubToastOptions): void {
    const card = document.createElement('div');
    // Rises in rather than appearing — the fade below only ever covered leaving.
    card.className = `${PLAQUE} animate-verdict pointer-events-auto w-full px-3 py-2.5 flex items-center gap-3`;
    card.style.transition = `opacity ${FADE_MS}ms ease`;

    const tick = document.createElement('div');
    tick.className = 'shrink-0 w-6 h-6 rounded-full border border-[#2b2219] bg-[#d8ab5c] '
      + 'text-[#241a10] text-[13px] leading-none font-bold flex items-center justify-center';
    tick.textContent = '✓';

    const text = document.createElement('div');
    text.className = 'min-w-0 flex-1 flex flex-col gap-0.5';

    const heading = document.createElement('div');
    heading.className = `font-heading font-bold text-[12px] ${LABEL}`;
    heading.style.color = '#d8ab5c';
    heading.textContent = options.heading;

    const title = document.createElement('div');
    title.className = `text-[13px] font-semibold leading-tight truncate ${LABEL}`;
    title.textContent = options.title;

    text.append(heading, title);

    if (options.detailHtml || options.detail) {
      const detail = document.createElement('div');
      detail.className = `text-[11px] font-semibold ${LABEL}`;
      detail.style.color = '#e8cea0';
      // Markup wins, but only ever markup this module's own callers built —
      // never a server string, which is why there is no escaping here.
      if (options.detailHtml) detail.innerHTML = options.detailHtml;
      else detail.textContent = options.detail!;
      text.appendChild(detail);
    }

    card.append(tick, text);

    let dismissTimer: ReturnType<typeof setTimeout> | null = null;
    const dismiss = (): void => {
      card.style.opacity = '0';
      const removal = setTimeout(() => { card.remove(); this.timers.delete(removal); }, FADE_MS);
      this.timers.add(removal);
    };

    if (options.action) {
      const button = document.createElement('button');
      button.type = 'button';
      // min-h-11 / min-w-11 is CLAUDE.md's 44px tap-target floor. This is the
      // one interactive thing in a notification layer and it is aimed at on a
      // phone, so it is the last place to shave a few pixels.
      button.className = 'shrink-0 min-h-11 min-w-11 px-3 rounded-md border border-[#6b573f] '
        + 'bg-[linear-gradient(180deg,#e6c079,#c99a45_55%,#8f6c28)] text-[#241a10] '
        + 'text-[13px] font-bold hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed';
      button.textContent = options.action.label;
      button.addEventListener('click', () => {
        button.disabled = true;
        // The card stops timing out the moment it is acted on: a reward
        // request that takes a second must not have its own toast vanish
        // underneath it, and a failure has to stay visible to be read.
        if (dismissTimer) { clearTimeout(dismissTimer); this.timers.delete(dismissTimer); dismissTimer = null; }
        options.action!.run()
          .then(() => dismiss())
          .catch((err: unknown) => {
            button.disabled = false;
            title.textContent = err instanceof Error ? err.message : t('That did not work — try the Quests panel.');
          });
      });
      card.appendChild(button);
    }

    this.root.appendChild(card);
    while (this.root.children.length > MAX_VISIBLE) this.root.firstElementChild?.remove();

    dismissTimer = setTimeout(() => {
      this.timers.delete(dismissTimer!);
      dismissTimer = null;
      dismiss();
    }, HOLD_MS);
    this.timers.add(dismissTimer);
  }

  dispose(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    this.root.remove();
  }
}
